import {
  FLIGHT_FARE_SELECTION_CACHE_KEY,
  FLIGHT_FILTER_CACHE_KEY,
  FLIGHT_RESULT_CACHE_KEY,
  FLIGHT_RT_SELECTION_CACHE_KEY,
  getFlightSearchCacheDuration,
} from "../config/constants";

/**
 * Short lived cache for a completed flight search, so a page refresh (or coming
 * back from the customer details page) reuses the results instead of re-running
 * the whole airline fan out. Stored in sessionStorage: it must survive a reload
 * but never leak into another tab or a later visit.
 */

/**
 * Domestic and international are stored apart, so each keeps its own 10 minute
 * entry instead of the newer search overwriting the older one. Without this a
 * single slot held whichever search ran last: picking a fare on a domestic
 * search and then running an international one left nothing to restore when the
 * traveller went back to the domestic result.
 *
 * The flight type is the first segment of the search key - see
 * buildFlightSearchCacheKey below, which is what these keys are built by.
 */
const FLIGHT_TYPE_NAMESPACES = ["d", "i"];
const DEFAULT_FLIGHT_TYPE_NAMESPACE = "d";

const namespaceOf = (searchKey) => {
  const flightType = String(searchKey ?? "").split("|")[0];
  return FLIGHT_TYPE_NAMESPACES.includes(flightType)
    ? flightType
    : DEFAULT_FLIGHT_TYPE_NAMESPACE;
};

const scopedKey = (storageKey, searchKey) =>
  `${storageKey}:${namespaceOf(searchKey)}`;

/** The window this search's entries live for - domestic and international are
 *  configured apart, and the flight type is already in the key. */
const durationFor = (searchKey) =>
  getFlightSearchCacheDuration(namespaceOf(searchKey));

const readEntry = (storageKey, searchKey) => {
  try {
    const raw = sessionStorage.getItem(scopedKey(storageKey, searchKey));
    if (!raw) return null;

    const entry = JSON.parse(raw);
    if (!entry || entry.searchKey !== searchKey || !entry.savedAt) return null;

    if (Date.now() - entry.savedAt > durationFor(searchKey)) {
      sessionStorage.removeItem(scopedKey(storageKey, searchKey));
      return null;
    }
    return entry.data ?? null;
  } catch (err) {
    try {
      sessionStorage.removeItem(scopedKey(storageKey, searchKey));
    } catch (removeErr) {
      // storage unavailable - nothing else to do
    }
    return null;
  }
};

const writeEntry = (storageKey, searchKey, data) => {
  try {
    sessionStorage.setItem(
      scopedKey(storageKey, searchKey),
      JSON.stringify({ searchKey, savedAt: Date.now(), data }),
    );
    return true;
  } catch (err) {
    // Quota exceeded or storage disabled: drop the stale entry and carry on
    // without a cache rather than breaking the search.
    try {
      sessionStorage.removeItem(scopedKey(storageKey, searchKey));
    } catch (removeErr) {
      // storage unavailable - nothing else to do
    }
    return false;
  }
};

/** Drops one flight type's entry, or every one of them when no key is given. */
const removeEntry = (storageKey, searchKey) => {
  const keys =
    searchKey === undefined
      ? FLIGHT_TYPE_NAMESPACES.map((type) => `${storageKey}:${type}`)
      : [scopedKey(storageKey, searchKey)];

  keys.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (err) {
      // storage unavailable - nothing else to do
    }
  });
};

/**
 * Identifies one search; a different route/date/pax invalidates the cache.
 * The flight type leads deliberately: namespaceOf above reads it back off the
 * key to decide which slot the entry belongs in.
 */
export const buildFlightSearchCacheKey = ({
  flightType,
  flightRoute,
  departureDate,
  returnDate,
  nationality,
  b2bClassType,
  passengers,
}) =>
  [
    flightType,
    flightRoute,
    departureDate,
    returnDate,
    nationality,
    b2bClassType,
    passengers,
  ].join("|");

export const readCachedFlightResults = (searchKey) =>
  readEntry(FLIGHT_RESULT_CACHE_KEY, searchKey);

/**
 * When the cached results were stored, so a restored page can count down the
 * remainder of the same 10 minutes rather than starting a fresh window.
 */
export const readCachedFlightResultsSavedAt = (searchKey) => {
  try {
    const raw = sessionStorage.getItem(
      scopedKey(FLIGHT_RESULT_CACHE_KEY, searchKey),
    );
    if (!raw) return null;

    const entry = JSON.parse(raw);
    if (!entry || entry.searchKey !== searchKey || !entry.savedAt) return null;
    if (Date.now() - entry.savedAt > durationFor(searchKey)) return null;
    return entry.savedAt;
  } catch (err) {
    return null;
  }
};

export const writeCachedFlightResults = (searchKey, data) =>
  writeEntry(FLIGHT_RESULT_CACHE_KEY, searchKey, data);

export const readCachedFareSelection = (searchKey) =>
  readEntry(FLIGHT_FARE_SELECTION_CACHE_KEY, searchKey);

export const writeCachedFareSelection = (searchKey, data) =>
  writeEntry(FLIGHT_FARE_SELECTION_CACHE_KEY, searchKey, data);

export const clearCachedFareSelection = (searchKey) =>
  removeEntry(FLIGHT_FARE_SELECTION_CACHE_KEY, searchKey);

/**
 * Domestic round trip keeps its leg progress in memory only, so the return leg
 * results and both picked flights/fares are stored separately. The departure
 * leg results come from the plain result cache above - they are that search's
 * own result set.
 */
export const readCachedRoundTripSelection = (searchKey) =>
  readEntry(FLIGHT_RT_SELECTION_CACHE_KEY, searchKey);

export const writeCachedRoundTripSelection = (searchKey, data) =>
  writeEntry(FLIGHT_RT_SELECTION_CACHE_KEY, searchKey, data);

export const clearCachedRoundTripSelection = (searchKey) =>
  removeEntry(FLIGHT_RT_SELECTION_CACHE_KEY, searchKey);

/**
 * The filter panel's state - stops, cabin class, policies, the time and price
 * ranges. Stored the same way as everything else here, so the values a search
 * is being read with survive a hop to the customer details page and back, and
 * so a domestic search and an international one keep their own: they are
 * separate entries, in the tab that set them, for the same 10 minutes.
 */
export const readCachedFilters = (searchKey) =>
  readEntry(FLIGHT_FILTER_CACHE_KEY, searchKey);

export const writeCachedFilters = (searchKey, data) =>
  writeEntry(FLIGHT_FILTER_CACHE_KEY, searchKey, data);

/**
 * Drops this search's cache. Pass the search key to clear only its flight type -
 * a domestic search re-pricing must not throw away an international one that is
 * still inside its own 10 minutes. Called with nothing on the way back to the
 * home page, where every search is over.
 */
export const clearFlightSearchCache = (searchKey) => {
  removeEntry(FLIGHT_RESULT_CACHE_KEY, searchKey);
  removeEntry(FLIGHT_FARE_SELECTION_CACHE_KEY, searchKey);
  removeEntry(FLIGHT_RT_SELECTION_CACHE_KEY, searchKey);
  removeEntry(FLIGHT_FILTER_CACHE_KEY, searchKey);
};
